import { ComponentProps } from "react";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type TopStoriesProps = {
  /** Additional class names for styling. */
  className?: string;
  /** The list of card properties to display. */
  cardProps: ComponentProps<typeof Card>[];
};

/**
 * Top stories component that displays a prominent article followed by additional articles for withHighlights template.
 * Figma: https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=2096-14883&t=m7103yrVxON0jRlB-4
 */
const TopStories = ({
  cardProps,
  className,
}: TopStoriesProps): React.ReactElement => {
  if (cardProps.length === 0) return <></>;

  const firstArticleVariant: CardVariations = {
    image: {
      position: "",
      width: "w-full",
      filters: { w: 960, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-full",
      extraClass: "pt-4",
    },
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6 "),
    },
    title: {
      color: "text-gray-850",
      size: "text-lg lg:text-7xl",
      extraClass: cn("font-bold font-lct"),
    },
  };

  const otherVariant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      position: "left",
      width: "w-full lg:w-1/2",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: 900, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: "pl-4",
    },
    title: {
      size: "text-lg",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
  };

  return (
    <div
      data-testid="template-with-highlight-top-stories-component"
      className={className}
    >
      <div className="stories grid grid-cols-1 gap-8 lg:grid-cols-2 lg:grid-rows-3">
        {cardProps.map((card, index) => {
          return (
            <Card
              key={card.id}
              {...card}
              variations={index === 0 ? firstArticleVariant : otherVariant}
              className={cn("relative", {
                "block lg:row-span-3": index === 0,
                "lg:after:absolute lg:after:-right-4 lg:after:top-0 lg:after:h-full lg:after:w-[1px] lg:after:bg-gray-175":
                  index === 0,
                "before:absolute before:-bottom-4 before:left-0 before:h-[1px] before:w-full before:bg-gray-175 lg:before:hidden":
                  index === 0,
                "after:absolute after:-bottom-4 after:left-0 after:h-[1px] after:w-full after:bg-gray-175":
                  index !== 0 && index !== cardProps.length - 1,
              })}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TopStories;
